// The video studio's Actions (live's Actions panel, #227): its 13 cards, "Function" and "Tool",
// in live's order and words (`video-form-shell/actions-panel/constants.ts`). A Function changes
// how the composer generates; a Tool makes its own job type, on the composer as live's edit and
// extend do (`job`, G7-tool-forms), or in its own form, as live's form switch picks one (`form`),
// and its issue adds it here with its catalog rows. Each card counts the catalog's models that
// take it, by live's filter over the model's mode blocks (`action-filters.ts`, G6 section 3), so
// no list here is kept by hand.
import {
  BetweenVerticalStart,
  Frame,
  Hash,
  Languages,
  MicVocal,
  MoveDiagonal,
  PencilLine,
  PersonStanding,
  Scissors,
  SmilePlus,
  Text,
  Timer,
  Video,
  type LucideIcon,
} from "lucide-react";

import type { CatalogVideoModel, GenerationType, VideoMode } from "../../../api/generated/model";
// Live's card previews, its own files unchanged (`colortv-web/src/assets/images/video-page/
// actions-images/`, eJam's assets): each card plays its own while hovered.
import startEndFramePreview from "./previews/start-end-frame.webm";
import multiShotPreview from "./previews/multi-shot.webm";
import multiInputPreview from "./previews/multi-Input.webm";
import videoEditPreview from "./previews/video-edit.webm";
import videoExtendPreview from "./previews/video-extend.webm";
import referencedGenerationPreview from "./previews/referenced-generation.webm";
import talkingHeadPreview from "./previews/talking-head.webm";
import videoUpscalePreview from "./previews/video-upscale.webm";
import editBackgroundPreview from "./previews/edit-background.webm";
import motionControlPreview from "./previews/motion-control.webm";
import videoDubbingPreview from "./previews/video-dubbing.webm";
import lipSyncPreview from "./previews/lip-sync.webm";
import videoReframePreview from "./previews/video-reframe.webm";

/** The tools with a form of their own (G7-tool-forms), by the job type each makes. */
export type ToolFormType =
  | "video.upscale"
  | "video.reframe"
  | "video.background"
  | "video.motion"
  | "video.talking_head"
  | "video.dub"
  | "video.lipsync";

/** Live's two kinds of card: a Function changes how the composer generates, a Tool replaces it. */
export type ToolKind = "function" | "tool";

export interface ActionTool {
  /** Live's action id ("startEndFrame", "videoUpscale"). */
  id: string;
  /** The card's title and line, in live's words. */
  title: string;
  description: string;
  kind: ToolKind;
  /** Live's 20px card icon, also the chip's. */
  icon: LucideIcon;
  /** Live's looping preview of the card, a WebM file it shows while hovered. */
  preview: string;
  /** Whether a model takes the action: the card's count, and the composer's models once chosen. */
  takes: (model: CatalogVideoModel) => boolean;
  /** The composer does what the card asks; a card whose issue is open would be Coming soon. */
  ready: boolean;
  /**
   * A Tool that runs on the composer (live's edit and extend collections): the job type it
   * makes, on that type's rows, and live's words when Start is pressed without its video.
   */
  job?: { type: GenerationType; needsVideo: string };
  /**
   * A Tool with a form of its own in place of the composer (live's dedicated forms): its job
   * type, whose rows are its models.
   */
  form?: ToolFormType;
}

/** The types whose blocks live's function cards read: its text, image and reference to video
 *  collections, never a tool's (an edit's references are not Multi-Input's). */
const FUNCTION_BLOCKS = ["video.text", "video.image", "video.reference"] as const;

/** The model's mode blocks the function cards read. */
const blocks = (model: CatalogVideoModel): VideoMode[] =>
  FUNCTION_BLOCKS.map((type) => model.modes[type]).filter((block) => block !== undefined);

const some = (test: (block: VideoMode) => boolean) => (model: CatalogVideoModel) =>
  blocks(model).some(test);

const takesFrames = some(
  (block) =>
    block.start_frame !== undefined && block.start_frame !== "none" && block.end_frame === true,
);
const takesShots = some((block) => block.multi_shot !== undefined && block.multi_shot !== null);
const takesReferences = some(
  (block) =>
    (block.reference_images?.max ?? 0) > 0 ||
    (block.reference_videos?.max ?? 0) > 0 ||
    (block.reference_audios?.max ?? 0) > 0,
);
// Audio tags do not count, as on live.
const takesTags = some(
  (block) => block.tags?.some((tag) => tag === "image" || tag === "video" || tag === "element") ?? false,
);
/** A Tool's models: the rows of its job type (live's tool collections, one per row). */
const rowsOf = (type: GenerationType) => (model: CatalogVideoModel) =>
  model.job_types.includes(type);

/** Every action the panel offers, in live's order. */
export const ACTION_TOOLS: readonly ActionTool[] = [
  {
    id: "startEndFrame",
    title: "Start & End Frame",
    description: "Generate between two defined frames.",
    kind: "function",
    icon: BetweenVerticalStart,
    preview: startEndFramePreview,
    takes: takesFrames,
    ready: true,
  },
  {
    id: "multiPrompt",
    title: "Multi-Shot",
    description: "Create a multi-shot video by prompting each shot individually",
    kind: "function",
    icon: Text,
    preview: multiShotPreview,
    takes: takesShots,
    ready: true,
  },
  {
    id: "multiInput",
    title: "Multi-Input",
    description: "Use a reference video, image or audio to guide motion",
    kind: "function",
    icon: Video,
    preview: multiInputPreview,
    takes: takesReferences,
    ready: true,
  },
  {
    id: "videoEdit",
    title: "Video Edit",
    description: "Edit or transform an existing video.",
    kind: "tool",
    icon: Scissors,
    preview: videoEditPreview,
    takes: rowsOf("video.edit"),
    ready: true,
    job: {
      type: "video.edit",
      needsVideo: "Video reference is required for edit video generation",
    },
  },
  {
    id: "videoExtend",
    title: "Video Extend",
    description: "Continue and expand an existing video.",
    kind: "tool",
    icon: Timer,
    preview: videoExtendPreview,
    takes: rowsOf("video.extend"),
    ready: true,
    job: {
      type: "video.extend",
      needsVideo: "Video reference is required for extend video generation",
    },
  },
  {
    id: "referencedGeneration",
    title: "Referenced Generation",
    description: "Generate videos using # tagged image",
    kind: "function",
    icon: Hash,
    preview: referencedGenerationPreview,
    takes: takesTags,
    ready: true,
  },
  {
    id: "talkingHead",
    title: "Talking Head",
    description: "Human-like AI actor with synced speech and expressive delivery",
    kind: "tool",
    icon: SmilePlus,
    preview: talkingHeadPreview,
    takes: rowsOf("video.talking_head"),
    ready: true,
    form: "video.talking_head",
  },
  {
    id: "videoUpscale",
    title: "Video Upscale",
    description: "Upscale video",
    kind: "tool",
    icon: MoveDiagonal,
    preview: videoUpscalePreview,
    takes: rowsOf("video.upscale"),
    ready: true,
    form: "video.upscale",
  },
  {
    id: "editBackground",
    title: "Edit Background",
    description: "Remove / Replace Background",
    kind: "tool",
    icon: PencilLine,
    preview: editBackgroundPreview,
    takes: rowsOf("video.background"),
    ready: true,
    form: "video.background",
  },
  {
    id: "motionControl",
    title: "Motion Control",
    description: "Guide camera or character motion using a reference video.",
    kind: "tool",
    icon: PersonStanding,
    preview: motionControlPreview,
    takes: rowsOf("video.motion"),
    ready: true,
    form: "video.motion",
  },
  {
    id: "videoDubbing",
    title: "Video Dubbing",
    description: "Create natural-sounding dubs in any language.",
    kind: "tool",
    icon: Languages,
    preview: videoDubbingPreview,
    takes: rowsOf("video.dub"),
    ready: true,
    form: "video.dub",
  },
  {
    id: "lipSync",
    title: "Lip-Sync",
    description: "Match lip movements to any audio for realistic speaking videos.",
    kind: "tool",
    icon: MicVocal,
    preview: lipSyncPreview,
    takes: rowsOf("video.lipsync"),
    ready: true,
    form: "video.lipsync",
  },
  {
    id: "videoReframe",
    title: "Reframe video",
    description: "Convert your videos to any aspect ratio without destructive cropping",
    kind: "tool",
    icon: Frame,
    preview: videoReframePreview,
    takes: rowsOf("video.reframe"),
    ready: true,
    form: "video.reframe",
  },
];

/** The action with this id, or null. */
export function actionOf(id: string | null): ActionTool | null {
  return ACTION_TOOLS.find((action) => action.id === id) ?? null;
}
